/* Courted · the small pieces: icons from the shell's sprite, the plate (Google's photo, or a drawn stand-in
   while it comes or when there isn't one), a page over Courted, a sheet, a toast, the domovoi with his map. */
import React from 'react';
import * as S from './sync.js';
import MAP from './anim/map.svg?raw';
import BAR from './anim/art-bar.svg?raw';
import DINNER from './anim/art-dinner.svg?raw';
import DINNER2 from './anim/art-dinner2.svg?raw';
import DINNER3 from './anim/art-dinner3.svg?raw';
import FALLS from './anim/art-falls.svg?raw';
import LOOKOUT from './anim/art-lookout.svg?raw';
import TRAIL from './anim/art-trail.svg?raw';
import MARSH from './anim/art-marsh.svg?raw';
import GALLERY from './anim/art-gallery.svg?raw';
import CINEMA from './anim/art-cinema.svg?raw';
import JAZZ from './anim/art-jazz.svg?raw';
import BOWLING from './anim/art-bowling.svg?raw';
import POOL from './anim/art-pool.svg?raw';

const { useState, useEffect, useRef } = React;

export function track(name, props) { if (window.DVTrack) window.DVTrack(name, Object.assign({ product: 'courted' }, props)); }
export const Ico = ({ n, s, style }) => <svg className={s ? 'co-ico-s' : 'co-ico'} style={style} aria-hidden="true"><use href={'#' + n} /></svg>;
const reduced = () => { try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (e) { return false; } };

/* A drawn stand-in for a kind of place, never pretending to be the place. */
const ART = { restaurant: [DINNER, DINNER2, DINNER3], bar: [BAR], fun: [BOWLING, POOL], culture: [GALLERY, CINEMA, JAZZ], out: [GALLERY, CINEMA, JAZZ], outdoors: [FALLS, LOOKOUT, TRAIL, MARSH] };
const byType = { art_gallery: GALLERY, art_museum: GALLERY, museum: GALLERY, movie_theater: CINEMA, performing_arts_theater: CINEMA, live_music_venue: JAZZ, jazz_club: JAZZ,
  bowling_alley: BOWLING, hiking_area: LOOKOUT, nature_preserve: MARSH, park: TRAIL, waterfall: FALLS };
function artFor(id, kind, type) {
  if (type && byType[type]) return byType[type];
  const list = ART[kind] || ART.culture;
  let h = 0; for (const c of String(id || '')) h = (h * 31 + c.charCodeAt(0)) >>> 0;
  return list[h % list.length];
}
// every copy gets its own ids, so a gradient never points into a copy that's hidden (a pane not showing)
let N = 0;
export const uniq = (svg) => { const n = ++N; return svg.replace(/id="([^"]+)"/g, (m, a) => 'id="' + a + '-' + n + '"').replace(/url\(#([^)]+)\)/g, (m, a) => 'url(#' + a + '-' + n + ')'); };
export function Art({ id, kind, type }) {
  const html = React.useMemo(() => uniq(artFor(id, kind, type)), [id, kind, type]);
  return <span className="co-art" aria-hidden="true" dangerouslySetInnerHTML={{ __html: html }} />;
}

/* Google's photo for a card, fetched only when `load` (the card being looked at), with its author. */
export function Plate({ card, kind, load, className, by }) {
  const [url, setUrl] = useState(null);
  const name = card && card.photo && card.photo.name;
  useEffect(() => {
    let off = false;
    setUrl(null);
    if (load && name) S.photo(name).then((u) => { if (!off) setUrl(u); });
    return () => { off = true; };
  }, [load, name]);
  const who = card && card.photo && card.photo.by && card.photo.by[0];
  return (
    <span className={'co-plate' + (className ? ' ' + className : '')}>
      <Art id={card && card.id} kind={kind} type={card && card.type} />
      {url ? <img src={url} alt="" draggable="false" referrerPolicy="no-referrer" /> : null}
      {url && by && who && who.name ? <span className="co-by">Photo: {who.name}</span> : null}
    </span>
  );
}

/* A page over Courted: its own scroll, a glass back button, the tabs still below. Escape goes back. */
export function Page({ onBack, back, right, children, className, label }) {
  const btn = useRef(null);
  useEffect(() => {
    // a sheet over the page takes Escape for itself (it closes; the page stays)
    const k = (e) => { if (e.key === 'Escape' && !document.querySelector('.co-sheet-wrap')) onBack(); };
    document.addEventListener('keydown', k);
    try { btn.current && btn.current.focus({ preventScroll: true }); } catch (e) {}
    return () => document.removeEventListener('keydown', k);
  }, [onBack]);
  return (
    <div className={'co-page' + (className ? ' ' + className : '')} role="dialog" aria-modal="true" aria-label={label}>
      <div className="co-page-top">
        <button ref={btn} type="button" className="dv-pill dv-pill-lg dv-glass co-back" onClick={onBack}><Ico n="i-back" s />{back}</button>
        <span style={{ flex: 1 }} />
        {right}
      </div>
      {children}
    </div>
  );
}

/* A sheet from the bottom, over a scrim. */
export function Sheet({ title, onClose, children, foot, cancel }) {
  useEffect(() => {
    const k = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', k);
    return () => document.removeEventListener('keydown', k);
  }, [onClose]);
  return (
    <div className="co-sheet-wrap">
      <div className="dv-scrim" onClick={onClose} />
      <section className="dv-sheet co-sheet" role="dialog" aria-modal="true" aria-label={title}>
        <span className="dv-grab" aria-hidden="true" />
        <div className="dv-sheet-head"><button type="button" className="dv-sheet-cancel" onClick={onClose}>{cancel || 'Cancel'}</button><h2 className="dv-sheet-title">{title}</h2></div>
        <div className="co-sheet-body">{children}</div>
        {foot ? <div className="dv-sheet-foot">{foot}</div> : null}
      </section>
    </div>
  );
}

export function useToast() {
  const [t, setT] = useState(null);
  const timer = useRef(null);
  const show = (text, ms) => { clearTimeout(timer.current); setT(text); timer.current = setTimeout(() => setT(null), ms || 2600); };
  useEffect(() => () => clearTimeout(timer.current), []);
  return [t ? <div className="dv-toast dv-glass co-toast" role="status">{t}</div> : null, show];
}

/* First time in: he unfolds the map, turns it the right way up and taps a spot as a pin pops in (3.2 s, once;
   tap to replay; reduced motion: the still). */
export function Mapper({ w }) {
  const [play, setPlay] = useState(() => !reduced());
  const again = () => { if (reduced()) return; setPlay(false); requestAnimationFrame(() => requestAnimationFrame(() => setPlay(true))); track('courted_mascot_tapped'); };
  const W = w || 190;
  const html = React.useMemo(() => uniq(MAP), []);
  return (
    <button type="button" className={'co-mapper' + (play ? ' play' : '')} aria-label="Play it again" onClick={again} style={{ width: W, height: Math.round(W * 968 / 937) }}>
      <span className="co-bd" dangerouslySetInnerHTML={{ __html: html }} />
    </button>
  );
}

/* Google's attribution, wherever its places (and, with T3, its weather) show without a map. */
export const Google = ({ weather }) => <p className="co-google">{weather ? 'Places and weather from ' : 'Places from '}<span translate="no">Google Maps</span></p>;

/* The day's forecast as a chip: Google's icon (its dark version in dark) and the top temperature. */
export function Wx({ d, country, words }) {
  if (!d) return null;
  const t = d.hi == null ? '' : Math.round(String(country || '').toUpperCase() === 'US' ? d.hi * 9 / 5 + 32 : d.hi) + '°';
  return (
    <span className="co-wx" title={words || d.text}>
      {d.icon ? <><img className="lt" src={d.icon + '.svg'} alt="" /><img className="dk" src={d.icon + '_dark.svg'} alt="" /></> : null}
      <span>{t}</span><span className="sr">{d.text}</span>
    </span>
  );
}
